import {eventHandlersMixin} from "@/components/events/mixins.js";
import {parseQueryParams, stringifyQueryParams} from "@/components/pacs/helpers.js";
import {QUERY_KEY_ONE_SCREEN_CAMERA_NUMBER, QUERY_KEY_ONE_SCREEN_TIME_SHIFT, QUERY_KEY_TABLE_PARAMS} from "@/router/queryKeys.js";
import {downloadCSV, ifChangedOnlyGlobalQueryKeys} from "@/utils/helpers.js";
import {ACTION_LOAD_CAMERA_FOR_PLAYER} from "@/store/cameras/index.js";
import {TOKEN_TTL} from "@/utils/consts.js";
import {downloadReportMixin} from "@/components/oneScreen/reports/downloadReportMixin.js";
import {yieldReportExport} from "@/utils/tableReportExport.js";

/**
 * Примесь с общими функциями для представления событий в таблицах.
 * Урезанная версия {@see entityTableMixin}, в отличие от которой в таблицах представлены честные JS-объекты типа {@see AnalyticMessage},
 * а не абстрактные объекты из API.
 */
export const eventTableMixin = {
  mixins: [
    eventHandlersMixin,
    downloadReportMixin,
  ],
  data() {
    const minEventArchiveFrom = new Date();
    minEventArchiveFrom.setDate(minEventArchiveFrom.getDate() - 33);
    return {
      isLoading: false,
      // Данные для PacsTable.vue
      columnNames: [],
      columnCaptions: {},
      filters: {}, // Информация о применяемых фильтрах.
      dataRows: [], // Строки с данными для таблицы.
      // Приватные переменные.
      $_entityTableMixin_initDone: false, // Флаг начальной инициализации компонента.
      archiveFrom: null,
      archiveTo: null,
      number: null,
      access: null,
      searchText: null,
      pageCount: 0,
      currentPage: 1,
      optionsPageSize: [20, 40, 60, 100, 200],
      pageSize: 60,
      minEventArchiveFrom: minEventArchiveFrom,
    };
  },
  watch: {
    /**
     * Перехват параметризованного URL - извлечение номера новой страницы и ее загрузка.
     * Хук beforeRouteUpdate работает только в компоненте, который непосредственно обслуживает маршрут,
     * но не его внутренние компоненты.
     *
     * @param {Object} to
     * @param {Object} from
     */
    $route(to, from) {
      if (ifChangedOnlyGlobalQueryKeys(from, to, [QUERY_KEY_TABLE_PARAMS])) {
        return;
      }
      this.assignQueryParams(parseQueryParams(to.query[QUERY_KEY_TABLE_PARAMS]));
      this.$_entityTableMixin_initDone && this.loadPage();
    },
  },
  /**
   * Настройка таблицы.
   */
  created() {
    // Все настройки переданные в URL накладываются поверх стандартных.
    this.assignQueryParams(parseQueryParams(this.$route.query[QUERY_KEY_TABLE_PARAMS]));
    // После всех настроек ставится микрозадача на завершение настроек, после чего произойдет первая загрузка данных в таблицу.
    queueMicrotask(this.$_entityTableMixin_finishInit);
  },
  methods: {
    async loadAllTableData(action, filters) {
      const allData = [];
      let page = 1;
      let allPages = this.isReportDownloadDialogOpen ? this.pageCount || 1 : this.pageCount;
      const originalPageSize = this.pageSize;
      const MAX_ROWS = 500000;
      const PAGE_SIZE = 1000;
      try {
        while (page <= allPages) {
          // Формируем запрос с динамическими параметрами, включая только те, которые заданы
          this.checkReportDownloadCancelled();
          const response = await this.$store.dispatch(action, {
            archiveFrom: this.clearArchiveFrom,
            archiveTo: this.clearArchiveTo,
            query: this.searchText,
            ...filters, // Передаем дополнительные фильтры, только если они заданы
            page,
            pageSize: PAGE_SIZE,
            ...this.getReportExportRequestConfig(),
          });
          this.checkReportDownloadCancelled();

          if (this.isReportDownloadDialogOpen && (!response?.page || !Array.isArray(response.results))) {
            throw new Error(this.$t("loadingError"));
          }
          if (this.isReportDownloadDialogOpen && allData.length + response.results.length > MAX_ROWS) {
            throw new Error(this.$t("tooManyRowsWarning", {max: MAX_ROWS}));
          }
          allData.push(...response.results);

          if (this.isReportDownloadDialogOpen) {
            allPages = response.page.all || 1;
            this.setReportExportProgress({page, pages: allPages, rows: allData.length});
          }

          if (!this.isReportDownloadDialogOpen && allData.length >= MAX_ROWS) {
            this.$camsdals.alert(this.$t("tooManyRowsWarning", {max: MAX_ROWS}));
            return allData.slice(0, MAX_ROWS);
          }

          if (response.page.current >= response.page.all) {
            break;
          }

          page++;
          await yieldReportExport();
          this.checkReportDownloadCancelled();
        }

        return allData;
      } catch (error) {
        if (this.isReportDownloadDialogOpen || error?.code === "REPORT_EXPORT_CANCELLED") throw error;
        this.$camsdals.alert(this.$t("loadingError"));
        return [];
      } finally {
        this.pageSize = originalPageSize;
      }
    },
    // Универсальная функция для выгрузки данных таблицы в CSV
    async downloadTableAsCSV(action, filenamePrefix, csvHeaders, formatDataForCSV, filters) {
      try {
        const allData = await this.loadAllTableData(action, filters);
        this.checkReportDownloadCancelled();
        let csvText = `${csvHeaders.join(',')}\r\n`;
        for (let index = 0; index < allData.length; index += 1) {
          if (index % 1000 === 0) {
            await yieldReportExport();
            this.checkReportDownloadCancelled();
          }
          const formattedRow = formatDataForCSV(allData[index]);
          csvText += `${formattedRow.join(',')}\r\n`;
        }
        const dateFrom = moment.tz(this.clearArchiveFrom, moment.tz.guess()).format("DD.MM.YYYY_HH.mm");
        const dateTo = moment.tz(this.clearArchiveTo, moment.tz.guess()).format("DD.MM.YYYY_HH.mm");
        const filename = `${filenamePrefix}_${dateFrom}_${dateTo}.csv`;
        this.checkReportDownloadCancelled();
        downloadCSV(filename, csvText);
        if (this.isReportDownloadDialogOpen) this.completeReportExport();
      } catch (error) {
        this.handleReportExportError(error);
      }
    },
    buildReportFilename(filenamePrefix, extension) {
      const dateFrom = moment.tz(this.clearArchiveFrom, moment.tz.guess()).format("DD.MM.YYYY_HH.mm");
      const dateTo = moment.tz(this.clearArchiveTo, moment.tz.guess()).format("DD.MM.YYYY_HH.mm");
      return `${filenamePrefix}_${dateFrom}_${dateTo}.${extension}`;
    },
    downloadTableAsXLSX(action, filenamePrefix, headers, rowBuilder, filters = {}) {
      const filename = this.buildReportFilename(filenamePrefix, "xlsx");
      const archiveFrom = this.clearArchiveFrom;
      const archiveTo = this.clearArchiveTo;
      const params = {
        archiveFrom: archiveFrom && new Date(archiveFrom),
        archiveTo: archiveTo && new Date(archiveTo),
        query: this.searchText,
        ...filters,
      };
      return this.downloadXLSXReport({
        filename,
        headers,
        rowBuilder,
        isCancelled: () => this._isDestroyed || this._isBeingDestroyed,
        loadPage: async ({page, pageSize}) => {
          this.checkReportDownloadCancelled();
          if (!params.archiveFrom || !params.archiveTo
            || !Number.isFinite(+params.archiveFrom) || !Number.isFinite(+params.archiveTo)) {
            throw new Error(this.$t('loadingError'));
          }
          const response = await this.$store.dispatch(action, {
            ...params,
            page,
            pageSize,
            isExport: true,
            ...this.getReportExportRequestConfig(),
          });
          this.checkReportDownloadCancelled();
          if (!response || !Array.isArray(response.results)) {
            throw new Error(this.$t('loadingError'));
          }
          return {rows: response.results, page: response.page};
        },
      }, "isLoading");
    },
    /**
     * Завершение инициализации и загрузка данных в таблицу.
     */
    $_entityTableMixin_finishInit() {
      this.$_entityTableMixin_initDone = true;
      this.loadPage();
    },
    /**
     * Ручное применение настроек таблицы.
     * Может быть перекрыто с целью уточнения какие именно настройки следует применить.
     */
    applyTableSettings() {
      this.stringifyQueryParams({});
    },
    /**
     * Приведет заданные настройки таблицы в строку для подстановки ее в адресную строку.
     *
     * Передача полученной строки в GET параметр и переход по готовому URL для применения настроек.
     * Переход по URL не осуществляется если новый и старый URL совпадают,
     * но при этом вызывается функция переданная в аргументе onAbort,
     * через нее принудительно вызывается разбор актуального URL для перезагрузки таблицы.
     *
     * @param {Object} queryParams
     */
    stringifyQueryParams(queryParams = {}) {
      this.$router.push(
        {name: this.$router.currentRoute.name, query: {[QUERY_KEY_TABLE_PARAMS]: stringifyQueryParams(queryParams)}},
        null,
        () => {
          this.assignQueryParams(parseQueryParams(this.$route.query[QUERY_KEY_TABLE_PARAMS]));
        }
      );
    },
    /**
     * Принимает разобранные параметры  GET строки для дальнейшей подстановки в компонент.
     * Какие параметры в какие переменные нужно подставить решается в перекрытом в компоненте методе.
     * Вызывать после осуществления навигации, для заполнения настроек переданными значениями или дефолтными.
     *
     * При присвоении настройкам таблицы значений из дефолтных, коими являются объекты - их необходимо присваивать
     * через полное, глубокое клонирование, чтобы избежать изменение оригинальных настроек, передающихся по ссылке.
     *
     * @param {Object} parsedQueryParams
     */
    // eslint-disable-next-line no-unused-vars
    assignQueryParams(parsedQueryParams) {
    },
    /**
     * Загрузка данных для таблицы по заданным настройкам.
     * Требует перекрытия в целевых компонентах.
     */
    async loadPage() {
    },
    /**
     * Получение информации по камере и открытие диалогового окна со скриншотом события.
     *
     * @param {AnalyticMessage} analyticMessage
     */
    async showFullScreenshotForEventInTable(analyticMessage) {
      const cameraInfo = await this.$store.cache.dispatch(`cameras/${ACTION_LOAD_CAMERA_FOR_PLAYER}`, {
        number: analyticMessage.cameraNumber,
        tokenLiveTTL: TOKEN_TTL.PLAYER,
        tokenDVRTTL: TOKEN_TTL.PLAYER,
      });
      if (!cameraInfo) {
        this.$camsdals.alert("Недостаточно прав для просмотра скриншота с этой камеры.");
        return;
      }
      this.showFullScreenshot(analyticMessage, cameraInfo);
    },
    /**
     * Получение информации по камере и скачивание видео события.
     *
     * @param {AnalyticMessage} analyticMessage
     */
    async downloadEventVideoInTable(analyticMessage) {
      const cameraInfo = await this.$store.cache.dispatch(`cameras/${ACTION_LOAD_CAMERA_FOR_PLAYER}`, {
        number: analyticMessage.cameraNumber,
        tokenLiveTTL: TOKEN_TTL.PLAYER,
        tokenDVRTTL: TOKEN_TTL.PLAYER,
      });
      if (!cameraInfo) {
        this.$camsdals.alert("Недостаточно прав для скачивания видео с этой камеры.");
        return;
      }
      this.downloadEventVideo(analyticMessage, cameraInfo);
    },
    /**
     * Открытие плеера и воспроизведение архива начиная с момента события.
     *
     * @param {AnalyticMessage} analyticMessage
     */
    async playEventStartForEventInTable(analyticMessage) {
      this.$router.push({
        name: this.$route.name,
        query: {
          ...(this.$route.query || {}),
          [QUERY_KEY_ONE_SCREEN_CAMERA_NUMBER]: analyticMessage.cameraNumber,
          [QUERY_KEY_ONE_SCREEN_TIME_SHIFT]: +analyticMessage.date,
        }
      });
    },
  }
};

import {
  ACTION_LOAD_CAMERAS,
  FIELDS_CAMERA,
} from "@/store/cameras/index.js";

/**
 * Примесь для общих данных и функций таблиц аналитик.
 */
export const entityAnalyticsTableMixin = {
  data() {
    return {
      camerasInfo: {},
      isLoadingCameras: false,
    };
  },
  methods: {
    getScreenshotUrl(camera, sizeScreenshot) {
      let frameUrl = `//${camera.server.domain}/${camera.number}/preview.mp4?token=${camera.token_l}`;
      if (camera.server.screenshot_domain) {
        frameUrl = `//${camera.server.screenshot_domain}/api/v0/screenshots/${camera.number}~${sizeScreenshot}.jpg?token=${camera.token_l}`;
      }
      return frameUrl;
    },
    async loadCamerasInfo(cameraNumbers) {
      if (!cameraNumbers || cameraNumbers.length === 0) {
        this.camerasInfo = {};
        return;
      }
      this.isLoadingCameras = true;
      try {
        const response = await this.$store.dispatch(`cameras/${ACTION_LOAD_CAMERAS}`, {
          fields: [
            FIELDS_CAMERA.number,
            FIELDS_CAMERA.address,
            FIELDS_CAMERA.title,
            FIELDS_CAMERA.server,
            FIELDS_CAMERA.token_l,
          ],
          numbers: cameraNumbers,
        });
        this.camerasInfo = response.reduce((acc, camera) => {
          const num = camera[FIELDS_CAMERA.number];
          acc[num] = {
            title: camera[FIELDS_CAMERA.title],
            address: camera[FIELDS_CAMERA.address],
            server: camera[FIELDS_CAMERA.server],
            token_l: camera.tokenLive || camera.token_l,
          };
          return acc;
        }, {});
      } catch (e) {
        console.error('Ошибка загрузки камер', e);
        this.camerasInfo = {};
      } finally {
        this.isLoadingCameras = false;
      }
    },
    getRowCameraNumber(dataRow) {
      const raw = dataRow.__raw;
      if (!this.entityStruct) return null;
      return raw?.[this.entityStruct.fields.camera_number.name];
    },
    getRowCameraTitle(dataRow) {
      const n = this.getRowCameraNumber(dataRow);
      const info = this.camerasInfo?.[n];
      return info?.title || "—";
    },
    getRowCameraAddress(dataRow) {
      const n = this.getRowCameraNumber(dataRow);
      const info = this.camerasInfo?.[n];
      return info?.address || "—";
    },
    getRowScreenshotUrl(dataRow) {
      const n = this.getRowCameraNumber(dataRow);
      const cam = this.camerasInfo?.[n];
      if (!cam) return "";
      return this.getScreenshotUrl(
        {
          number: String(n),
          title: cam.title,
          server: cam.server,
          token_l: cam.token_l,
        },
        "400"
      );
    },
    openOneScreenDialogFromRow(dataRow) {
      const cameraNumber = this.getRowCameraNumber(dataRow);

      this.$router.push({
        name: this.$route.name,
        query: {
          ...(this.$route.query || {}),
          [QUERY_KEY_ONE_SCREEN_CAMERA_NUMBER]: cameraNumber,
        },
      });
    },
  },
};
